import React from 'react';
import {
  Box,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  Divider,
  Avatar,
  useTheme,
} from '@mui/material';
import { LogOut, Moon, Sun, CreditCard, User, ChevronUp, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logoutUser } from '../../../store/slices/authSlice';
import { toggleTheme } from '../../../store/slices/themeSlice';
import { AppDispatch, RootState } from '../../../store';
import { formatUsername } from '../../../utils/userUtils';
import { useAuth } from '../../../hooks';
import { AUTH_CONSTANTS } from '../../../config/constants';

interface BottomMenuProps {
  isDrawerOpen: boolean;
}

const BottomMenu: React.FC<BottomMenuProps> = ({ isDrawerOpen }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { role } = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  const selectedOrganization = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation
  );
  const { user } = useSelector((state: RootState) => state.auth);
  const { mode } = useSelector((state: RootState) => state.theme);
  const username = user?.email ? formatUsername(user.email) : 'kishorekumaru';
  // Trial status - this would typically come from your subscription state
  const isTrialAccount = selectedOrganization?.isTrailEnabled;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const menuRef = React.useRef<HTMLDivElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    dispatch(logoutUser());
    navigate('/');
  };

  const handleThemeToggle = () => {
    handleMenuClose();
    dispatch(toggleTheme());
  };

  const handleSubscriptions = () => {
    handleMenuClose();
    navigate('/dashboard/subscriptions');
  };

  const handleProfile = () => {
    handleMenuClose();
    navigate('/dashboard/personalization');
  };

  return (
    <Box
      ref={menuRef}
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        height: '56px',
        backgroundColor: 'transparent',
        borderTop: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* User section with menu trigger */}
      <Tooltip
        title={!isDrawerOpen ? 'Settings & Account' : ''}
        placement="right"
        disableHoverListener={isDrawerOpen}
      >
        <ListItem
          button
          onClick={handleMenuOpen}
          sx={{
            width: '100%',
            height: '100%',
            px: 2,
            py: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              justifyContent: isDrawerOpen ? 'space-between' : 'center',
            }}
          >
            {isDrawerOpen ? (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
                  >
                    {username}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      Settings & Account
                    </Typography>
                    {isTrialAccount && (
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: 'bold',
                          color: theme.palette.common.white,
                          bgcolor: theme.palette.warning.main,
                          px: 0.8,
                          py: 0.2,
                          borderRadius: '4px',
                        }}
                      >
                        TRIAL
                      </Box>
                    )}
                  </Box>
                </Box>
                {open ? (
                  <ChevronUp size={16} color={theme.palette.text.secondary} />
                ) : (
                  <ChevronDown size={16} color={theme.palette.text.secondary} />
                )}
              </>
            ) : (
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: isTrialAccount ? theme.palette.warning.main : theme.palette.primary.main,
                  fontSize: '0.875rem',
                  position: 'relative',
                }}
              >
                {username?.charAt(0).toUpperCase()}
                {isTrialAccount && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      width: 12,
                      height: 12,
                      bgcolor: theme.palette.warning.main,
                      borderRadius: '50%',
                      border: `2px solid ${theme.palette.background.paper}`,
                    }}
                  />
                )}
              </Avatar>
            )}
          </Box>
        </ListItem>
      </Tooltip>

      {/* Dropdown Menu */}
      <Menu
        anchorEl={anchorEl ? menuRef.current : null}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: -1,
            borderRadius: 0,
            width: 'auto',
            minWidth: '180px',
            maxWidth: '180px',
            left: '0 !important',
            right: '0 !important',
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0px 5px 15px rgba(0, 0, 0, 0.6), 0px 3px 8px rgba(0, 0, 0, 0.5)'
                : '0px 5px 15px rgba(0, 0, 0, 0.3), 0px 3px 8px rgba(0, 0, 0, 0.2)',
            '& .MuiMenuItem-root': {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            },
          },
        }}
        style={{ width: 'auto' }}
        MenuListProps={{
          style: { width: '100%' },
        }}
      >
        <MenuItem onClick={handleProfile}>
          <ListItemIcon>
            <User size={16} />
          </ListItemIcon>
          <ListItemText primary="Profile" primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>

        {role !== AUTH_CONSTANTS.ROLES.USER ? (
          <MenuItem onClick={handleSubscriptions}>
            <ListItemIcon>
              <CreditCard size={16} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    width: '100%',
                    overflow: 'hidden',
                  }}
                >
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      flexShrink: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    Subscriptions
                  </Typography>
                  {isTrialAccount && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: '0.6rem',
                        fontWeight: 'bold',
                        color: theme.palette.common.white,
                        bgcolor: theme.palette.warning.main,
                        px: 0.5,
                        py: 0.1,
                        borderRadius: '4px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        ml: 'auto',
                      }}
                    >
                      TRIAL
                    </Box>
                  )}
                </Box>
              }
              primaryTypographyProps={{ variant: 'body2', component: 'div' }}
            />
          </MenuItem>
        ) : null}

        <MenuItem onClick={handleThemeToggle}>
          <ListItemIcon>{mode === 'dark' ? <Sun size={16} /> : <Moon size={16} />}</ListItemIcon>
          <ListItemText
            primary={mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleLogout} sx={{ color: theme.palette.error.main }}>
          <ListItemIcon sx={{ color: theme.palette.error.main }}>
            <LogOut size={16} />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{
              variant: 'body2',
              color: theme.palette.error.main,
            }}
          />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default BottomMenu;
